import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";

import { ActivatedRoute, Router } from "@angular/router";

import { finalize } from "rxjs/operators";

import {
  IntegrationsService,
  SquareBusiness,
  SquareConnection,
} from "../../../../shared/services/integrations.service";

import { SquareSetupComponent } from "./square/square-setup.component";

@Component({
  selector: "business-account-integrations",

  standalone: true,

  imports: [CommonModule, SquareSetupComponent],

  templateUrl: "./integrations.component.html",

  styleUrl: "./integrations.component.scss",
})
export class BusinessAccountIntegrationsComponent implements OnInit {
  loading = true;

  connecting = false;

  disconnecting = false;

  squareConnected = false;

  square: SquareConnection | null = null;

  business: SquareBusiness | null = null;

  successMessage = "";

  errorMessage = "";

  /*
   * false:
   * integrations dashboard
   *
   * true:
   * Square setup/configuration
   */
  configuringSquare = false;

  constructor(
    private readonly integrationsService: IntegrationsService,

    private readonly route: ActivatedRoute,

    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    const squareResult = this.route.snapshot.queryParamMap.get("square");

    if (squareResult === "connected") {
      this.successMessage = "Square connected successfully.";

      this.router.navigate([], {
        relativeTo: this.route,

        queryParams: {
          square: null,
        },

        queryParamsHandling: "merge",

        fragment: undefined,

        replaceUrl: true,
      });
    }

    this.loadSquareStatus();
  }

  loadSquareStatus(): void {
    this.loading = true;

    this.errorMessage = "";

    this.integrationsService
      .getSquareStatus()
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (response) => {
          this.squareConnected = response.connected;

          this.square = response.square ?? null;

          this.business = response.business ?? null;

          if (!response.connected) {
            this.configuringSquare = false;
          }
        },

        error: (error) => {
          console.error("Unable to load Square connection status:", error);

          this.squareConnected = false;

          this.square = null;

          this.configuringSquare = false;

          this.errorMessage =
            error?.error?.detail ?? "Unable to load your Square connection.";
        },
      });
  }

  connectSquare(): void {
    if (this.connecting) {
      return;
    }

    this.connecting = true;

    this.errorMessage = "";

    this.successMessage = "";

    this.integrationsService
      .connectSquare()
      .pipe(
        finalize(() => {
          this.connecting = false;
        }),
      )
      .subscribe({
        next: (response) => {
          if (!response.authorization_url) {
            this.errorMessage = "Square did not return an authorization URL.";

            return;
          }

          window.location.href = response.authorization_url;
        },

        error: (error) => {
          console.error("Unable to start Square connection:", error);

          this.errorMessage =
            error?.error?.detail ?? "Unable to connect Square right now.";
        },
      });
  }

  disconnectSquare(): void {
    if (this.disconnecting) {
      return;
    }

    const confirmed = window.confirm(
      "Disconnect Square from this business? " +
        "gloget will no longer be able to access " +
        "this Square account.",
    );

    if (!confirmed) {
      return;
    }

    this.disconnecting = true;

    this.errorMessage = "";

    this.successMessage = "";

    this.integrationsService
      .disconnectSquare()
      .pipe(
        finalize(() => {
          this.disconnecting = false;
        }),
      )
      .subscribe({
        next: () => {
          this.squareConnected = false;

          this.square = null;

          this.configuringSquare = false;

          this.successMessage = "Square has been disconnected.";
        },

        error: (error) => {
          console.error("Unable to disconnect Square:", error);

          this.errorMessage =
            error?.error?.detail ?? "Unable to disconnect Square right now.";
        },
      });
  }

  openSquareSetup(): void {
    if (!this.squareConnected) {
      return;
    }

    this.successMessage = "";

    this.errorMessage = "";

    this.configuringSquare = true;
  }

  closeSquareSetup(): void {
    this.configuringSquare = false;

    this.loadSquareStatus();
  }

  refreshSquareStatus(): void {
    this.successMessage = "";

    this.loadSquareStatus();
  }

  get merchantDisplayName(): string {
    return (
      this.square?.merchant_name || this.business?.name || "Square account"
    );
  }

  get environmentLabel(): string {
    if (!this.square?.environment) {
      return "";
    }

    const value = this.square.environment.toLowerCase();

    return value === "sandbox" ? "Sandbox" : "Production";
  }

  get isSandbox(): boolean {
    return this.square?.environment?.toLowerCase() === "sandbox";
  }
}
